<template>
  <transition name="banner-slide">
    <section v-if="shouldRender" class="holiday-banner" :class="{ 'holiday-banner--preload': !readyToShow }"
      :style="bannerStyle">
      <div class="holiday-banner__content">
        <div class="holiday-banner__info">
          <img v-if="bannerConfig.leftIcon" :src="leftIconUrl" alt="" class="holiday-banner__icon">
          <div class="holiday-banner__text">
            <span class="holiday-banner__headline">{{ bannerTitle }}</span>
            <span class="holiday-banner__dates">
              <span class="holiday-banner__preposition">{{ $t('holidayBannerFrom') }}</span>
              <span class="holiday-banner__date">{{ formattedStartDate }}</span>
              <span class="holiday-banner__preposition">{{ $t('holidayBannerTo') }}</span>
              <span class="holiday-banner__date">{{ formattedEndDate }}</span>
              <img v-if="bannerConfig.tailIcon" :src="tailIconUrl" alt="" class="holiday-banner__tail-icon">
            </span>
          </div>
        </div>
        <div class="holiday-banner__actions">
          <button type="button" class="holiday-banner__cta" @click="handleCta">
            {{ $t('holidayBannerCta') }}
          </button>
        </div>
      </div>
      <button type="button" class="holiday-banner__close" :aria-label="$t('holidayBannerClose')" @click="handleClose">
        <span class="holiday-banner__close-icon" aria-hidden="true"></span>
      </button>
      <div v-if="isAuthModalOpen" class="holiday-banner__modal-backdrop" @click.self="isAuthModalOpen = false">
        <div class="holiday-banner__modal">
          <p class="holiday-banner__modal-title">{{ $t('holidayBannerAuthTitle') }}</p>
          <p class="holiday-banner__modal-text">{{ $t('holidayBannerAuthText') }}</p>
          <button type="button" class="holiday-banner__modal-btn" @click="isAuthModalOpen = false">
            {{ $t('holidayBannerAuthAction') }}
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
export default {
  name: "HolidayBanner",
  data() {
    return {
      now: Date.now(),
      hiddenUntil: 0,
      timerId: null,
      delayTimerId: null,
      readyToShow: false,
      isAuthModalOpen: false,
      authDismissed: false,
      dateOptions: { 
        day: "2-digit", 
        month: "2-digit" 
      },
      bannerConfig: {
        holiday: "cristmas",
        background: "",
        startDate: "2025-12-24T00:00:00Z",
        endDate: "2026-01-03T23:59:59Z",
        reopenAfterHours: 24,
        bannerId: "main-layout-banner",
        leftIcon: "cristmas_tree_ball-47percente.svg",
        tailIcon: "⏰.png",
      },
    }
  },
  computed: {
    backgroundUrl() {
      if (this.bannerConfig.background) {
        return this.bannerConfig.background
      }
      if (this.bannerConfig.holiday) {
        return `https://services.adsquiz.io/service_files/offers-banners/bg/banner-${this.bannerConfig.holiday}.png`
      }
      return ""
    },
    leftIconUrl() {
      if (this.bannerConfig.leftIcon) {
        return `https://services.adsquiz.io/service_files/offers-banners/icons/${this.bannerConfig.leftIcon}`
      }
      return ""
    },
    tailIconUrl() {
      if (this.bannerConfig.tailIcon) {
        return `https://services.adsquiz.io/service_files/offers-banners/icons/${this.bannerConfig.tailIcon}`
      }
      return ""
    },
    bannerStyle() {
      const style = {
      }
      if (this.backgroundUrl) {
        style.backgroundImage = `url(${this.backgroundUrl})`
        style.backgroundSize = "cover"
        style.backgroundPosition = "16% center"
      } else {
        style.backgroundColor = "#3749C0"
      }
      return style
    },
    startMs() {
      return this.normalizeToMs(this.bannerConfig.startDate)
    },
    endMs() {
      return this.normalizeToMs(this.bannerConfig.endDate)
    },
    bannerTitleKey() {
      if (!this.startMs || !this.endMs) {
        return 'holidayBannerDefaultTitle'
      }
      const startYear = new Date(this.startMs).getFullYear()
      const endYear = new Date(this.endMs).getFullYear()
      return startYear === endYear ? 'holidayBannerDefaultTitle' : 'holidayBannerTitleLastOffer'
    },
    bannerTitle() {
      return this.$t(this.bannerTitleKey)
    },
    shouldRender() {
      if (this.isAuthPage() && this.authDismissed) {
        return false
      }
      if (!this.startMs || !this.endMs) {
        return false
      }
      if (this.now < this.startMs || this.now >= this.endMs) {
        return false
      }
      if (this.hiddenUntil && this.now < this.hiddenUntil) {
        return false
      }
      return true
    },
    storageKey() {
      const suffix = this.bannerConfig.bannerId || "banner"
      return `holiday_banner_hidden_until_${suffix}`
    },
    formattedStartDate() {
      return this.formatDate(this.startMs)
    },
    formattedEndDate() {
      return this.formatDate(this.endMs)
    }
  },
  mounted() {
    this.hiddenUntil = this.readHiddenUntil()
    this.now = Date.now()
    this.timerId = window.setInterval(() => {
      this.now = Date.now()
    }, 60000)
    this.delayTimerId = window.setTimeout(() => {
      this.readyToShow = true
    }, 3000)
  },
  beforeUnmount() {
    if (this.timerId) {
      clearInterval(this.timerId)
    }
    if (this.delayTimerId) {
      clearTimeout(this.delayTimerId)
    }
  },
  methods: {
    handleCta() {
      if (this.isAuthPage()) {
        this.isAuthModalOpen = true
      } else {
        this.$router.push({ name: 'tariffs-page' })
      }
    },
    handleClose() {
      if (this.isAuthPage()) {
        this.authDismissed = true
        return
      }
      const hours = Number.isFinite(this.bannerConfig.reopenAfterHours) ? this.bannerConfig.reopenAfterHours : 72
      const reopenMs = Math.max(hours, 0) * 60 * 60 * 1000
      const hiddenUntil = Date.now() + reopenMs
      this.hiddenUntil = hiddenUntil
      this.persistHiddenUntil(hiddenUntil)
      this.now = Date.now()
    },
    normalizeToMs(value) {
      if (value === null || value === undefined) {
        return null
      }
      if (value instanceof Date) {
        return value.getTime()
      }
      const numeric = Number(value)
      if (!Number.isNaN(numeric)) {
        if (typeof value === "number" && value < 1e12) {
          return numeric * 1000
        }
        return numeric
      }
      const parsed = Date.parse(value)
      return Number.isNaN(parsed) ? null : parsed
    },
    dateLocale() {
      const map = {
        UA: 'uk-UA',
        EN: 'en-US',
        RU: 'ru-RU',
        DE: 'de-DE',
        PT: 'pt-PT',
        ES: 'es-ES',
        PL: 'pl-PL',
      }
      return map[this.$i18n.locale] || 'en-US'
    },
    formatDate(timestamp) {
      if (!timestamp) {
        return ""
      }
      try {
        return new Date(timestamp).toLocaleDateString(this.dateLocale, this.dateOptions)
      } catch (error) {
        return new Date(timestamp).toLocaleDateString()
      }
    },
    readHiddenUntil() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (raw) {
          const parsed = parseInt(raw, 10)
          return Number.isFinite(parsed) ? parsed : 0
        }
      } catch (error) {
        return 0
      }
      return 0
    },
    persistHiddenUntil(value) {
      try {
        localStorage.setItem(this.storageKey, String(value))
      } catch (error) {
        // ignore storage errors
      }
    },
    isAuthPage() {
      const route = this.$route || {}
      const name = (route.name || '').toString().toLowerCase()
      const path = (route.path || '').toLowerCase()
      const authNames = ['login', 'login-view', 'signin', 'sign-in', 'register', 'signup', 'sign-up', 'register-view', 'signup-view']
      return authNames.includes(name) || /login|register|sign-?up/.test(path)
    },
  }
}
</script>

<style scoped>
.holiday-banner {
  width: 100%;
  box-sizing: border-box;
  color: #fff;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  z-index: 3999;
  opacity: 1;
  transition: transform 0.35s ease, opacity 0.35s ease;
  padding: 4px 24px;
}

.holiday-banner--preload {
  opacity: 0;
  transform: translateY(-22px);
  visibility: hidden;
}

.holiday-banner__content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 30px;
}

.holiday-banner__info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
}

.holiday-banner__icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
  animation: holiday-icon-sway 7s ease-in-out infinite;
}

.holiday-banner__text {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
}

.holiday-banner__headline {
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  align-items: center;
}

.holiday-banner__dates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-weight: 700;
}

.holiday-banner__tail-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

@keyframes holiday-icon-sway {

  0%,
  60%,
  100% {
    transform: translateX(0) rotate(0deg);
  }

  8% {
    transform: translateX(-5px) rotate(-8deg);
  }

  16% {
    transform: translateX(5px) rotate(8deg);
  }

  24% {
    transform: translateX(-3px) rotate(-6deg);
  }

  32% {
    transform: translateX(2px) rotate(4deg);
  }

  40% {
    transform: translateX(-1px) rotate(-3deg);
  }
}

.holiday-banner__actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.holiday-banner__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 100px;
  border: 1px solid #fff;
  background: #fff;
  color: #1e2bb8;
  text-decoration: none;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
  animation: holiday-cta-shake 4.5s ease-in-out infinite;
}

.holiday-banner__cta:hover {
  box-shadow: 0 10px 24px rgba(30, 43, 184, 0.18);
  animation-play-state: paused;
}

.holiday-banner__cta:active {
  transform: translateY(0);
  box-shadow: 0 6px 16px rgba(30, 43, 184, 0.14);
}

@keyframes holiday-cta-shake {

  0%,
  10%,
  90%,
  100% {
    transform: translate(0, 0);
  }

  3% {
    transform: translate(-1px, -1px) rotate(-1deg);
  }

  5% {
    transform: translate(1px, 1px) rotate(1deg);
  }

  7% {
    transform: translate(-1.5px, 0.5px) rotate(-1deg);
  }

  45%,
  55% {
    transform: translate(0, 0);
  }

  48% {
    transform: translate(-1px, -1px) rotate(-1deg);
  }

  50% {
    transform: translate(1px, 1px) rotate(1deg);
  }

  52% {
    transform: translate(-1.5px, 0.5px) rotate(-1deg);
  }
}

.holiday-banner__close {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 32px;
  height: 32px;
  border: none;
  padding: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.holiday-banner__close-icon {
  position: relative;
  width: 14px;
  height: 14px;
  display: block;
}

.holiday-banner__close-icon::before,
.holiday-banner__close-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 2px;
  background: #fff;
  transform-origin: center;
}

.holiday-banner__close-icon::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.holiday-banner__close-icon::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.holiday-banner__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  height: 100%;
}

.holiday-banner__modal {
  background: #fff;
  color: #0f172a;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  max-width: 360px;
  width: 90%;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.holiday-banner__modal-title {
  font-weight: 700;
  font-size: 18px;
  margin: 0 0 8px;
  text-align: center;
}

.holiday-banner__modal-text {
  margin: 0 0 16px;
  line-height: 1.45;
  text-align: center;
}

.holiday-banner__modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 36px;
  border-radius: 10px;
  background: #1e2bb8;
  color: #fff;
  border: none;
  font-weight: 700;
  cursor: pointer;
}

.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: all 0.25s ease;
}

.banner-slide-enter-from,
.banner-slide-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 768px) {
  .holiday-banner {
    padding: 8px 24px;
  }
  .holiday-banner__text {
    flex-direction: column;
    width: max-content;
  }

  .holiday-banner__actions {
    width: 100%;
    justify-content: flex-start;
  }

  .holiday-banner__cta {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .holiday-banner {
    padding: 16px 24px;
  }
  .holiday-banner__content {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
